import React, { useEffect, useRef, useState } from 'react';
import { currency } from '../utils/formatters';

// InfoTooltip Component
function InfoTooltip({ content, inline = false }) {
  const [visible, setVisible] = useState(false);
  
  return (
    <div style={{ position: inline ? 'relative' : 'absolute', top: inline ? 'auto' : '12px', right: inline ? 'auto' : '12px', display: inline ? 'inline-block' : 'block' }}>
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          border: '1.5px solid rgba(14,79,86,0.4)',
          color: 'rgba(14,79,86,0.7)',
          fontSize: '11px',
          fontWeight: '700',
          cursor: 'help',
          transition: 'all 0.2s ease',
          backgroundColor: visible ? 'rgba(14,79,86,0.1)' : 'transparent'
        }}
      >
        i
      </span>
      {visible && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: '0',
            marginTop: '8px',
            backgroundColor: '#2a2a2a',
            color: '#ffffff',
            padding: '10px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            lineHeight: '1.5',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            pointerEvents: 'auto',
            minWidth: '200px',
            maxWidth: '400px',
            whiteSpace: 'normal'
          }}
        >
          {content}
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              right: '12px',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid #2a2a2a'
            }}
          />
        </div>
      )}
    </div>
  );
}

function ResultsPanel({ response, status, showDetails, onToggleDetails, onCopyJson, onDownloadCsv, inputs }) {
  const totalPaymentRef = useRef(null);
  const achievementScoreRef = useRef(null);
  const efficiencyScoreRef = useRef(null);
  const qualityScoreRef = useRef(null);
  
  const prevScoresRef = useRef({
    achievement: 0,
    efficiency: 0,
    quality: 0
  });

  useEffect(() => {
    if (response && status === 'success') {
      const total = response?.totals ? ((response.totals.upside_total || 0) + (response.totals.downside_total || 0)) : 0;
      
      if (totalPaymentRef.current) {
        animateNumber(totalPaymentRef.current, 0, total, 1000, currency);
      }
    }
  }, [response, status]);

  // Animate scores
  useEffect(() => {
    if (response && status === 'success' && response.scores) {
      const achievementScore = response.scores.achievementScore || 0;
      const efficiencyScore = response.scores.efficiencyScore || 0;
      const qualityScore = response.scores.qualityScore || 0;

      if (achievementScoreRef.current) {
        animateNumber(achievementScoreRef.current, prevScoresRef.current.achievement, achievementScore, 800, (val) => Math.round(val));
      }
      if (efficiencyScoreRef.current) {
        animateNumber(efficiencyScoreRef.current, prevScoresRef.current.efficiency, efficiencyScore, 800, (val) => Math.round(val));
      }
      if (qualityScoreRef.current) {
        animateNumber(qualityScoreRef.current, prevScoresRef.current.quality, qualityScore, 800, (val) => Math.round(val));
      }

      // Update previous scores
      prevScoresRef.current = {
        achievement: achievementScore,
        efficiency: efficiencyScore,
        quality: qualityScore
      };
    }
  }, [response, status]);

  const animateNumber = (el, start, end, duration = 800, formatter = (val) => val) => {
    const startTime = performance.now();
    const diff = end - start;
    
    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const val = start + diff * ease;
      el.textContent = formatter(val);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // Check if inputs match center averages (Performance Y1)
  const checkIfMaintainingMetrics = () => {
    if (!response?.scores) return false;
    
    const numTransplantsMatch = inputs.numTransplants === response.scores.centerTransplants;
    const acceptRateMatch = Math.abs(inputs.offerAcceptRate - response.scores.centerOfferAcceptRate) < 0.01;
    const graftSurvivalMatch = Math.abs(inputs.graftSurvival - response.scores.centerGraftSurvival) < 0.1;
    
    return numTransplantsMatch && acceptRateMatch && graftSurvivalMatch;
  };

  const isMaintainingMetrics = checkIfMaintainingMetrics();
  const totalPayment = response?.totals ? ((response.totals.upside_total || 0) + (response.totals.downside_total || 0)) : 0;

  // Determine which payment type to show (upside or downside)
  const upsidePerTransplant = response?.perTransplant?.upside ?? 0;
  const downsidePerTransplant = response?.perTransplant?.downside ?? 0;
  const showUpsidePayment = upsidePerTransplant !== 0;
  const showDownsidePayment = downsidePerTransplant !== 0;

  return (
    <>
      {/* Main Focus: Total Payment */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{ textAlign: 'center', position: 'relative' }}>
          <div style={{ fontSize: '16px', color: 'rgba(0,0,0,0.6)', fontWeight: '600', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Estimated Payment (from CMS)</div>
          <div 
            ref={totalPaymentRef}
            className="animated-number"
            style={{
              fontSize: '64px', fontWeight: '900', marginTop: '8px',
              color: response?.totals && ((response.totals.upside_total || 0) + (response.totals.downside_total || 0)) < 0 ? '#ef4444' : '#cd7029'
            }}
          >
            $0
          </div>
          
          {/* Show non-zero upside or downside payment underneath */}
          {response && (showUpsidePayment || showDownsidePayment) && (
            <div style={{ 
              marginTop: '12px', 
              fontSize: '16px', 
              color: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span>
                {showUpsidePayment ? (
                  <>Upside Payment per Transplant: <strong style={{ color: '#cd7029' }}>{currency(upsidePerTransplant)}</strong></>
                ) : (
                  <>Downside Payment per Transplant: <strong style={{ color: downsidePerTransplant < 0 ? '#ef4444' : '#cd7029' }}>{currency(downsidePerTransplant)}</strong></>
                )}
              </span>
              <InfoTooltip 
                inline={true}
                content={
                  showUpsidePayment 
                    ? `$15,000 × (Score − 60) / 40` 
                    : `$2,000 × (40 - Score) / 40`
                } 
              />
            </div>
          )}
        </div>
      </div>

      {/* Explanation Card */}
      {response && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '16px', lineHeight: '1.6', color: 'rgba(0,0,0,0.75)', padding: '8px 0' }}>
            {isMaintainingMetrics ? (
              <>
                If you maintain your current year metrics into the next performance year,
                {totalPayment >= 0 ? (
                  <> your center will <strong>receive approximately {currency(totalPayment)}</strong> from CMS as part of the IOTA model.</>
                ) : (
                  <> your center will <strong style={{ color: '#ef4444' }}>owe approximately {currency(Math.abs(totalPayment))}</strong> to CMS as part of this mandatory payment model.</>
                )}
              </>
            ) : (
              <>
                Based on your projected metrics,
                {totalPayment >= 0 ? (
                  <> your center would <strong style={{ color: '#cd7029' }}>receive approximately {currency(totalPayment)}</strong> from CMS.</>
                ) : (
                  <> your center would <strong style={{ color: '#ef4444' }}>owe approximately {currency(Math.abs(totalPayment))}</strong> to CMS as part of this mandatory payment model.</>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Three Main Scores - Always Visible */}
      {response && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' }}>
          {/* Achievement Score */}
          <div style={{ position: 'relative', textAlign: 'center' }}>
            {response?.scores && (
              <div style={{ position: 'absolute', top: '0', right: '0' }}>
                <InfoTooltip content={`The target (${Math.round(response.scores.transplantTarget)}) is calculated by applying the national growth rate (${(response.scores.nationalGrowthRate * 100).toFixed(1)}%) to the average of the 3 baseline years. ${response.scores.currentTransplants} is ${Math.round(response.scores.currentTransplants / response.scores.transplantTarget * 100, 3)}% of the transplant target, corresponding to a score of ${response.scores.achievementScore}.`} />
              </div>
            )}
            <div style={{ fontSize: '13px', color: 'rgba(0,0,0,0.6)', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Achievement Score</div>
            <div style={{ fontSize: '48px', fontWeight: '800', color: response?.scores?.achievementScore === 60 ? '#0e4f56' : 'rgba(0,0,0,0.4)' }}>
              <span ref={achievementScoreRef}>0</span><span style={{ fontSize: '28px', color: 'rgba(0,0,0,0.4)' }}>/60</span>
            </div>
          </div>

          {/* Efficiency Score */}
          <div style={{ position: 'relative', textAlign: 'center' }}>
            {response?.scores && response?.efficiencyBreakdown && (
              <div style={{ position: 'absolute', top: '0', right: '0' }}>
                {response.efficiencyBreakdown.improvement > response.efficiencyBreakdown.achievement ? (
                  <InfoTooltip content={`A ${inputs.offerAcceptRate} offer acceptance rate is ${response.efficiencyBreakdown.isAboveBenchmark ? 'above' : 'below'} benchmark ${Math.round(response.scores.benchmarkAcceptanceRate * 100)}%, ${response.efficiencyBreakdown.isAboveBenchmark ? 'leading to an automatic 15 points' : 'so the score is calculated as an improvement from the third baseline year.'}, corresponding to an efficiency score of ${response?.scores?.efficiencyScore}.`} />
                ) : (
                  <InfoTooltip content={`A ${inputs.offerAcceptRate} offer acceptance rate is at the ${response.scores.acceptancePercentile}th percentile, which corresponds to an achievement score of ${response?.scores?.efficiencyScore}.`} />
                )}
              </div>
            )}
            <div style={{ fontSize: '13px', color: 'rgba(0,0,0,0.6)', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Efficiency Score</div>
            <div style={{ fontSize: '48px', fontWeight: '800', color: response?.scores?.efficiencyScore === 20 ? '#0e4f56' : 'rgba(0,0,0,0.4)' }}>
              <span ref={efficiencyScoreRef}>0</span><span style={{ fontSize: '28px', color: 'rgba(0,0,0,0.4)' }}>/20</span>
            </div>
            {response?.efficiencyBreakdown && (
              <div style={{ fontSize: '11px', color: 'rgba(0,0,0,0.5)', marginTop: '4px' }}>
                Achieve: {Math.round(response?.efficiencyBreakdown?.achievement ?? 0)} | Improve: {Math.round(response?.efficiencyBreakdown?.improvement ?? 0)}
              </div>
            )}
          </div>

          {/* Quality Score */}
          <div style={{ position: 'relative', textAlign: 'center' }}>
            {response?.scores && (
              <div style={{ position: 'absolute', top: '0', right: '0' }}>
                <InfoTooltip content={`A ${inputs.graftSurvival}% graft survival rate is at the ${response.scores.graftSurvivalPercentile}th percentile, which corresponds to a quality score of ${response?.scores?.qualityScore}.`} />
              </div>
            )}
            <div style={{ fontSize: '13px', color: 'rgba(0,0,0,0.6)', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quality Score</div>
            <div style={{ fontSize: '48px', fontWeight: '800', color: response?.scores?.qualityScore === 20 ? '#0e4f56' : 'rgba(0,0,0,0.4)' }}>
              <span ref={qualityScoreRef}>0</span><span style={{ fontSize: '28px', color: 'rgba(0,0,0,0.4)' }}>/20</span>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details - Collapsible (removed upside/downside, moved above) */}
      
      {showDetails && response?.payments && (
        <div style={{marginTop: '12px'}}>
          <div className="card" style={{padding: '12px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <strong>Detailed Per-Transplant Payments</strong>
              <div className="small-muted">Rows limited to first 200 for performance</div>
            </div>

            <div style={{marginTop: '10px', overflow: 'auto', maxHeight: '260px', borderRadius: '8px'}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead style={{position: 'sticky', top: 0, background: 'rgba(248,250,252,0.95)', backdropFilter: 'blur(2px)'}}>
                  <tr>
                    <th style={{textAlign: 'left', padding: '8px', fontSize: '13px', borderBottom: '1px solid rgba(0,0,0,0.1)'}}>#</th>
                    <th style={{textAlign: 'left', padding: '8px', fontSize: '13px', borderBottom: '1px solid rgba(0,0,0,0.1)'}}>Transplant ID</th>
                    <th style={{textAlign: 'right', padding: '8px', fontSize: '13px', borderBottom: '1px solid rgba(0,0,0,0.1)'}}>Upside ($)</th>
                    <th style={{textAlign: 'right', padding: '8px', fontSize: '13px', borderBottom: '1px solid rgba(0,0,0,0.1)'}}>Downside ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {response.payments.slice(0, 200).map((p, idx) => (
                    <tr key={idx}>
                      <td style={{padding: '8px', borderBottom: '1px solid rgba(0,0,0,0.05)'}}>{idx + 1}</td>
                      <td style={{padding: '8px', borderBottom: '1px solid rgba(0,0,0,0.05)'}}>{p.id || ''}</td>
                      <td style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid rgba(0,0,0,0.05)'}}>{currency(p.upside)}</td>
                      <td style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid rgba(0,0,0,0.05)'}}>{currency(p.downside)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ResultsPanel;
